/**
 * What the chat offers as buttons between the transcript and the composer. Each one is sent as the user's
 * own message, the retry included, so this decides only which ones are worth offering.
 */

/**
 * Retrying is only offered where it can work. A context window that overflowed is the same size on the next
 * attempt, so a retry there is a button that visibly does nothing in the one case the user most needs an
 * explanation instead.
 *
 * Where it is offered, the retry asks the agent to have another go in the user's own voice, as a message
 * like any other. That is what people already typed when a run stalled, and what the agent already
 * answers well.
 */
export const isRetryableError = (errorCode: string | null): boolean =>
    errorCode !== null
    && errorCode !== ''
    && errorCode !== 'agent_input_context_too_large'
    // A stale thread was already refreshed in place, and the draft is back in the composer to be sent.
    && errorCode !== 'thread_stale'
    // The local history is what was rejected, so sending it again is rejected the same way. That message
    // asks for a reload, which is the only thing that helps.
    && errorCode !== 'invalid_thread_messages'
    // A local failure to open the upload. No run is waiting, so a message to the agent is about nothing.
    && errorCode !== 'context_upload_unavailable'
    // The run has not started yet rather than failed. It still holds the message, so sending it again
    // would ask the same thing twice.
    && errorCode !== 'agent_run_still_queued';

/** Whether the agent's question offers an upload, read from its own working memory. */
export const offersUploadOf = (state: unknown): boolean =>
    (state as Record<string, unknown> | null)?.offerUpload === true;

/**
 * The answers the agent offered for the question it just asked, read from its own working memory. Anything
 * that is not a non-empty string is dropped rather than rendered as a button that sends nothing.
 */
export const suggestedAnswersOf = (state: unknown): string[] => {
    const answers = (state as Record<string, unknown> | null)?.suggestedAnswers;
    if (!Array.isArray(answers)) {
        return [];
    }

    return answers
        .filter((answer): answer is string => typeof answer === 'string')
        .map(answer => answer.trim())
        .filter(answer => answer !== '');
};
